/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { imguser } from "../../imagepath";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import config from "../../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SidebarAdmin from "../../SidebarAdmin";
import BusinessHeader from "../../BusinessHeader";
import { useForm } from "react-hook-form";
import axios from "axios";
import Select from "react-select";

const EditProfileBusiness = () => {
  const userId = localStorage.getItem("userUUID");
  const businessId = localStorage.getItem("BusinessID");
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [instituteTypeOptions, setInstituteTypeOptions] = useState([]);
  const [specializationOptions, setSpecializationOptions] = useState([]);
  const [vanityStatus, setVanityStatus] = useState(null);
  const [isPinCodeTouched, setIsPinCodeTouched] = useState(false);

  const [formData, setFormData] = useState({
    institution_name: "",
    vanity_url: "",
    institute_type: "",
    specializations: "",
    email: "",
    phone: "", // Simplified to single phone field
    additional_phones: "",
    additional_emails: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pin_code: "",
    gst: "",
    pan_card: "",
    udyam_Aadhaar: "",
    profile_image: null,
  });

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  // Fetch business data
  useEffect(() => {
    if (userId && businessId) {
      fetch(`${config.API_URL}get_businessdata.php?business_id=${businessId}`)
        .then((response) => response.json())
        .then((data) => {
          console.log("Fetch business data response:", data);
          if (data.success === "1") {
            const business = data.business;
            const userData = {
              institution_name: business.institution_name || "",
              vanity_url: business.vanity_url || "",
              institute_type: business.institute_type || "",
              specializations: business.specializations || "",
              email: business.email || "",
              phone: business.mobile_number || "", // Use mobile_number as phone
              additional_phones: business.additional_phones || "",
              additional_emails: business.additional_emails || "",
              address_line1: business.address_line1 || "",
              address_line2: business.address_line2 || "",
              city: business.city || "",
              state: business.state || "",
              pin_code: business.pin_code || "",
              gst: business.gst || "",
              pan_card: business.pan_card || "",
              udyam_Aadhaar: business.udyam_Aadhaar || "",
              profile_image: business.profile_image
                ? `${config.API_URL}uploads/profile_images/business_images/${business.profile_image}`
                : null,
            };
            setFormData(userData);
            Object.keys(userData).forEach((key) => setValue(key, userData[key]));
            setLoading(false);
          } else {
            toast.error(data.message || "Failed to fetch business data.");
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching business data:", error);
          toast.error("Error fetching business data.");
          setLoading(false);
        });
    } else {
      toast.error("User ID or Business ID not found.");
      setLoading(false);
      navigate("/login");
    }
  }, [userId, businessId, navigate, setValue]);

  // Fetch institute types
  useEffect(() => {
    axios
      .get(`${config.API_URL}get_institutes.php`)
      .then((response) => {
        if (Array.isArray(response.data.institutes)) {
          const options = response.data.institutes.map((name) => ({
            value: name,
            label: name,
          }));
          setInstituteTypeOptions(options);
        } else {
          console.error("Invalid institute API response");
        }
      })
      .catch((error) => console.error("Error fetching institutes:", error));
  }, []);

  // Fetch specializations based on institute type
  useEffect(() => {
    if (formData.institute_type) {
      axios
        .get(`${config.API_URL}get_specializations.php?institute=${formData.institute_type}`)
        .then((response) => {
          if (Array.isArray(response.data.specializations)) {
            const options = response.data.specializations.map((spec) => ({
              value: spec,
              label: spec,
            }));
            setSpecializationOptions(options);
          } else {
            console.error("Invalid specialization response");
          }
        })
        .catch((error) => console.error("Error fetching specializations:", error));
    }
  }, [formData.institute_type]);

  // Check vanity URL availability
  useEffect(() => {
    if (!formData.vanity_url) return;
    const delayDebounceFn = setTimeout(() => {
      if (formData.vanity_url !== formData.vanity_url) {
        checkVanityUrlAvailability(formData.vanity_url);
      } else {
        setVanityStatus("");
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [formData.vanity_url]);

  const checkVanityUrlAvailability = async (url) => {
    try {
      const response = await fetch(`${config.API_URL}check_vanity_url.php`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ vanity_url: url }).toString(),
      });
      const data = await response.json();
      setVanityStatus(data.status === "taken" ? "taken" : "available");
    } catch (error) {
      console.error("Error checking vanity URL:", error);
      setVanityStatus(null);
    }
  };

  // Fetch city and state based on PIN code
  const fetchCityAndState = async (pinCode) => {
    if (pinCode.length !== 6) return;
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pinCode}`);
      const data = await response.json();
      if (data && data[0]?.Status === "Success" && data[0]?.PostOffice?.length) {
        const { District, State } = data[0].PostOffice[0];
        if (!District || !State) {
          setFormData((prev) => ({ ...prev, city: "", state: "" }));
          toast.error("Invalid Pin Code.");
        } else {
          setFormData((prev) => ({ ...prev, city: District, state: State }));
          setValue("city", District);
          setValue("state", State);
          clearErrors("pin_code");
        }
      } else {
        setFormData((prev) => ({ ...prev, city: "", state: "" }));
        setError("pin_code", { type: "manual", message: "Invalid Pin Code." });
      }
    } catch (error) {
      console.error("Error fetching city and state:", error);
      toast.error("Error fetching location details.");
    }
  };

  // Handle PIN code change
  const handlePinCodeChange = (e) => {
    let pinCode = e.target.value.replace(/\D/g, "");
    setIsPinCodeTouched(true);
    if (pinCode.length < 6) {
      setError("pin_code", { type: "manual", message: "Pin code must be 6 digits" });
    } else {
      clearErrors("pin_code");
    }
    if (pinCode.length > 6) pinCode = pinCode.slice(0, 6);
    setFormData((prev) => ({ ...prev, pin_code: pinCode }));
    setValue("pin_code", pinCode);
    if (pinCode.length === 6) fetchCityAndState(pinCode);
    else {
      setFormData((prev) => ({ ...prev, city: "", state: "" }));
      setValue("city", "");
      setValue("state", "");
    }
  };

  // Handle GST change
  const formatGSTNumber = (input) => {
    let formattedText = "";
    input = input.toUpperCase();
    for (let i = 0; i < input.length; i++) {
      if (i < 2) {
        if (/[0-9]/.test(input[i])) formattedText += input[i];
      } else if (i >= 2 && i < 7) {
        if (/[A-Z]/.test(input[i])) formattedText += input[i];
      } else if (i >= 7 && i < 11) {
        if (/[0-9]/.test(input[i])) formattedText += input[i];
      } else if (i === 11) {
        if (/[A-Z]/.test(input[i])) formattedText += input[i];
      } else if (i === 12) {
        if (/[0-9]/.test(input[i])) formattedText += input[i];
      } else if (i === 13) {
        formattedText += "Z";
      } else if (i === 14) {
        if (/[A-Z0-9]/.test(input[i])) formattedText += input[i];
      }
    }
    return formattedText;
  };

  const handleGSTChange = (e) => {
    let value = e.target.value.toUpperCase().trim();
    let formattedValue = formatGSTNumber(value);
    setFormData((prev) => ({ ...prev, gst: formattedValue }));
    setValue("gst", formattedValue);
    if (formattedValue.length === 15) {
      const panFromGst = formattedValue.substring(2, 12);
      setFormData((prev) => ({ ...prev, pan_card: panFromGst }));
      setValue("pan_card", panFromGst);
      clearErrors("gst");
    } else {
      setError("gst", { type: "manual", message: "GST must be 15 characters" });
    }
  };

  // Handle Udyam Aadhaar change
  const formatUdyamNumber = (input) => {
    input = input.toUpperCase();
    let formattedText = "UDYAM";
    for (let i = 5; i < input.length; i++) {
      if (i === 5) formattedText += "-";
      else if (i > 5 && i < 8) {
        if (/[A-Z]/.test(input[i])) formattedText += input[i];
      } else if (i === 8) formattedText += "-";
      else if (i > 8 && i < 11) {
        if (/[0-9]/.test(input[i])) formattedText += input[i];
      } else if (i === 11) formattedText += "-";
      else if (i > 11 && i < 19) {
        if (/[0-9]/.test(input[i])) formattedText += input[i];
      }
    }
    return formattedText;
  };

  const handleUdyamChange = (e) => {
    let value = e.target.value.toUpperCase().trim();
    let formattedValue = formatUdyamNumber(value);
    setFormData((prev) => ({ ...prev, udyam_Aadhaar: formattedValue }));
    setValue("udyam_Aadhaar", formattedValue);
  };

  // Handle input changes
  const onInputChange = (e) => {
    const { name, value } = e.target;
    let transformedValue = value;
    if (name === "institution_name" || name === "address_line1" || name === "address_line2") {
      transformedValue = value
        .split(/(\s+|\()/g)
        .map((part, index, arr) =>
          part.length > 0
            ? part === "(" && index < arr.length - 1
              ? "("
              : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            : part
        )
        .join("");
    } else if (name === "vanity_url") {
      transformedValue = value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    }
    setFormData((prev) => ({ ...prev, [name]: transformedValue }));
    setValue(name, transformedValue);
  };

  // Handle institute type change
  const handleSelectChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, institute_type: value, specializations: "" }));
    setValue("institute_type", value);
    setValue("specializations", "");
  };

  // Handle specializations change
  const handleSpecializationsChange = (selectedOptions) => {
    const value = selectedOptions ? selectedOptions.map((opt) => opt.value).join(", ") : "";
    setFormData((prev) => ({ ...prev, specializations: value }));
    setValue("specializations", value);
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const uploadData = new FormData();
    uploadData.append("business_id", businessId);
    uploadData.append("profile_image", file);
    try {
      const response = await fetch(`${config.API_URL}update-business-profile-image.php`, {
        method: "POST",
        body: uploadData,
      });
      const result = await response.json();
      if (result.status === "success") {
        setFormData((prev) => ({ ...prev, profile_image: result.image_url }));
        localStorage.setItem("profileImage", result.image_url);
        toast.success("Profile image updated successfully!");
      } else {
        toast.error(result.message || "Failed to upload image.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error uploading image.");
    }
  };

  // Submit form
  const onSubmit = async (data) => {
    setIsSubmitting(true);

    // Validation
    if (!formData.institution_name || formData.institution_name.trim() === "") {
      toast.error("Please enter the Institution Name.");
      setIsSubmitting(false);
      return;
    }

    if (formData.pin_code && !/^[0-9]{6}$/.test(formData.pin_code)) {
      toast.error("Please enter a valid 6-digit pin code.");
      setIsSubmitting(false);
      return;
    }

    if (!formData.city || !formData.state) {
      toast.error("Invalid Pin Code. Please enter a correct pin code.");
      setIsSubmitting(false);
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      setIsSubmitting(false);
      return;
    }

    if (formData.gst && !/^[0-9A-Z]{15}$/.test(formData.gst)) {
      toast.error("Please enter a valid 15-character GST number (alphanumeric).");
      setIsSubmitting(false);
      return;
    }

    if (formData.udyam_Aadhaar && !/^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/.test(formData.udyam_Aadhaar)) {
      toast.error("Please enter a valid Udyam Aadhaar Number (e.g., UDYAM-XX-00-0000000).");
      setIsSubmitting(false);
      return;
    }

    if (vanityStatus === "taken") {
      toast.error("The vanity URL is already taken. Please choose another.");
      setIsSubmitting(false);
      return;
    }

    const submitData = new FormData();
    submitData.append("business_id", businessId);
    submitData.append("institution_name", formData.institution_name);
    submitData.append("vanity_url", formData.vanity_url);
    submitData.append("institute_type", formData.institute_type);
    submitData.append("specializations", formData.specializations);
    submitData.append("email", formData.email);
    submitData.append("mobile_number", formData.phone); // Send phone as mobile_number
    submitData.append("additional_phones", formData.additional_phones);
    submitData.append("additional_emails", formData.additional_emails);
    submitData.append("address_line1", formData.address_line1);
    submitData.append("address_line2", formData.address_line2);
    submitData.append("city", formData.city);
    submitData.append("state", formData.state);
    submitData.append("pin_code", formData.pin_code);
    submitData.append("gst", formData.gst);
    submitData.append("pan_card", formData.pan_card);
    submitData.append("udyam_Aadhaar", formData.udyam_Aadhaar);

    try {
      const response = await fetch(`${config.API_URL}update_business_profile.php`, {
        method: "POST",
        body: submitData,
      });
      const result = await response.json();
      console.log("Update business profile response:", result);
      if (
        result.success === "1" ||
        result.success === true ||
        result.success === "true" ||
        result.status === "success"
      ) {
        toast.success(result.message || "Business profile updated successfully!", {
          onClose: () => navigate("/admin-dashboard"),
        });
      } else {
        toast.error(result.message || "Failed to update business profile.");
      }
    } catch (error) {
      console.error("Error updating business profile:", error);
      toast.error("Error updating business profile.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />
      <BusinessHeader />
      <SidebarAdmin />
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Dashboard </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Edit Business Profile</li>
                </ul>
              </div>
            </div>
          </div>
          {/* /Page Header */}
          {loading ? (
            <div>Loading...</div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card-box">
                <h3 className="card-title">Business Information</h3>
                <div className="row">
                  <div className="col-md-12">
                    <div className="profile-img-wrap">
                      <img
                        className="inline-block"
                        src={formData.profile_image || imguser}
                        alt="business"
                      />
                      <div className="fileupload btn">
                        <span className="btn-text">edit</span>
                        <input
                          className="upload"
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                        />
                      </div>
                    </div>
                    <div className="profile-basic">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group local-forms">
                            <label className="focus-label">Institution Name *</label>
                            <input
                              type="text"
                              className="form-control floating"
                              name="institution_name"
                              {...register("institution_name", { required: "Institution Name is required" })}
                              value={formData.institution_name}
                              onChange={onInputChange}
                            />
                            {errors.institution_name && (
                              <span className="text-danger">{errors.institution_name.message}</span>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">Vanity URL</label>
                            <input
                              type="text"
                              className={`form-control floating ${
                                vanityStatus === "taken" ? "is-invalid" : vanityStatus === "available" ? "is-valid" : ""
                              }`}
                              name="vanity_url"
                              {...register("vanity_url")}
                              value={formData.vanity_url}
                              onChange={onInputChange}
                            />
                            {vanityStatus === "taken" && (
                              <span className="text-danger">This vanity URL is already taken.</span>
                            )}
                            {vanityStatus === "available" && (
                              <span className="text-success">This vanity URL is available!</span>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms" style={{ zIndex: 999 }}>
                            <label className="focus-label">Institute Type</label>
                            <Select
                              options={instituteTypeOptions}
                              placeholder="Select Institute Type"
                              onChange={handleSelectChange}
                              value={instituteTypeOptions.find(
                                (option) => option.value === formData.institute_type
                              )}
                              isClearable
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group local-forms" style={{ zIndex: 998 }}>
                            <label className="focus-label">Specializations</label>
                            <Select
                              isMulti
                              options={specializationOptions}
                              placeholder="Select Specializations"
                              onChange={handleSpecializationsChange}
                              value={specializationOptions.filter((option) =>
                                formData.specializations.includes(option.value)
                              )}
                              isClearable
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">Email *</label>
                            <input
                              type="email"
                              className="form-control floating"
                              name="email"
                              {...register("email", {
                                required: "Email is required",
                                pattern: {
                                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                  message: "Invalid email address",
                                },
                              })}
                              value={formData.email}
                              onChange={onInputChange}
                              disabled
                            />
                            {errors.email && <span className="text-danger">{errors.email.message}</span>}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">Mobile Number</label>
                            <input
                              type="text"
                              className="form-control floating"
                              name="phone"
                              {...register("phone")}
                              value={formData.phone}
                              onChange={onInputChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">Additional Phones</label>
                            <input
                              type="text"
                              className="form-control floating"
                              name="additional_phones"
                              {...register("additional_phones")}
                              value={formData.additional_phones}
                              onChange={onInputChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">Additional Emails</label>
                            <input
                              type="text"
                              className="form-control floating"
                              name="additional_emails"
                              {...register("additional_emails")}
                              value={formData.additional_emails}
                              onChange={onInputChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-box">
                <h3 className="card-title">Business Address</h3>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group local-forms">
                      <label className="focus-label">Address Line 1 *</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="address_line1"
                        {...register("address_line1", { required: "Address Line 1 is required" })}
                        value={formData.address_line1}
                        onChange={onInputChange}
                      />
                      {errors.address_line1 && (
                        <span className="text-danger">{errors.address_line1.message}</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group local-forms">
                      <label className="focus-label">Address Line 2</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="address_line2"
                        {...register("address_line2")}
                        value={formData.address_line2}
                        onChange={onInputChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group local-forms">
                      <label className="focus-label">Pin Code *</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="pin_code"
                        {...register("pin_code", {
                          required: "Pin Code is required",
                          pattern: {
                            value: /^[0-9]{6}$/,
                            message: "Pin code must be 6 digits",
                          },
                        })}
                        value={formData.pin_code}
                        onChange={handlePinCodeChange}
                      />
                      {errors.pin_code && <span className="text-danger">{errors.pin_code.message}</span>}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group local-forms">
                      <label className="focus-label">City *</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="city"
                        {...register("city", { required: "City is required" })}
                        value={formData.city}
                        readOnly
                      />
                      {errors.city && <span className="text-danger">{errors.city.message}</span>}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group local-forms">
                      <label className="focus-label">State *</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="state"
                        {...register("state", { required: "State is required" })}
                        value={formData.state}
                        readOnly
                      />
                      {errors.state && <span className="text-danger">{errors.state.message}</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-box">
                <h3 className="card-title">Tax Information</h3>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group local-forms">
                      <label className="focus-label">GST Number</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="gst"
                        {...register("gst", {
                          pattern: {
                            value: /^[0-9A-Z]{15}$/,
                            message: "GST must be 15 alphanumeric characters",
                          },
                        })}
                        value={formData.gst}
                        onChange={handleGSTChange}
                        maxLength={15}
                      />
                      {errors.gst && <span className="text-danger">{errors.gst.message}</span>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group local-forms">
                      <label className="focus-label">PAN Number</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="pan_card"
                        {...register("pan_card")}
                        value={formData.pan_card}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group local-forms">
                      <label className="focus-label">Udyam Aadhaar Number</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="udyam_Aadhaar"
                        {...register("udyam_Aadhaar", {
                          pattern: {
                            value: /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/,
                            message: "Invalid Udyam Aadhaar format (e.g., UDYAM-XX-00-0000000)",
                          },
                        })}
                        value={formData.udyam_Aadhaar}
                        onChange={handleUdyamChange}
                      />
                      {errors.udyam_Aadhaar && (
                        <span className="text-danger">{errors.udyam_Aadhaar.message}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-center m-t-20">
                <button
                  className="btn btn-primary submit-btn"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default EditProfileBusiness;